import { useState, useEffect } from 'react';
import { useRoutes, useCreateRoute } from '../hooks/useRoutes';
import { SessionAnalysisType } from '../types/SessionAnalysisType';
import { ImSpinner2 } from 'react-icons/im';
import { HiPlus, HiOutlineCheckCircle, HiOutlineXMark, HiCheck } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import FormEntry from './FormEntry';

interface VelodromeRouteSelectorProps {
  analysisType: number;
  selectedRouteId?: string;
  onRouteSelect?: (routeId: string | null) => void;
  className?: string;
}

export default function VelodromeRouteSelector({
  analysisType,
  selectedRouteId,
  onRouteSelect,
  className = '',
}: VelodromeRouteSelectorProps) {
  const [showNewVelodromeForm, setShowNewVelodromeForm] = useState<boolean>(false);
  const [newVelodromeName, setNewVelodromeName] = useState<string>('');
  const [newLapLength, setNewLapLength] = useState<string>('');
  const [newBendRadius, setNewBendRadius] = useState<string>('');
  const [newBankAngle, setNewBankAngle] = useState<string>('');

  const createRoute = useCreateRoute();

  const isVelodromeIndoors = Number(analysisType) === SessionAnalysisType.LOOP_VELODROME_INDOORS;
  const isVelodromeOutdoors = Number(analysisType) === SessionAnalysisType.LOOP_VELODROME_OUTDOORS;
  const isVelodrome = isVelodromeIndoors || isVelodromeOutdoors;

  // Determine the routes query string based on the selected analysis type
  const routesQueryString = isVelodromeIndoors
    ? 'scope=loop_velodrome_indoors'
    : isVelodromeOutdoors
      ? 'scope=loop_velodrome_outdoors'
      : '';

  // Fetch routes when velodrome type is selected
  const { data: routes, isLoading: isRoutesLoading } = useRoutes(routesQueryString);

  // Reset form when analysis type changes
  useEffect(() => {
    setShowNewVelodromeForm(false);
    setNewVelodromeName('');
    setNewLapLength('');
    setNewBendRadius('');
    setNewBankAngle('');
  }, [analysisType]);

  // Handle creating a new velodrome
  const handleCreateVelodrome = async () => {
    // Validation
    if (!newVelodromeName.trim()) {
      toast.error('Velodrome name is required');
      return;
    }
    const lapLength = parseFloat(newLapLength);
    const bendRadius = parseFloat(newBendRadius);
    const bankAngle = parseFloat(newBankAngle);

    if (isNaN(lapLength) || lapLength <= 0) {
      toast.error('Lap length must be a positive number');
      return;
    }
    if (isNaN(bendRadius) || bendRadius <= 0) {
      toast.error('Bend radius must be a positive number');
      return;
    }
    if (isNaN(bankAngle) || bankAngle < 0 || bankAngle > 90) {
      toast.error('Bank angle must be between 0 and 90 degrees');
      return;
    }

    const routeCategory = isVelodromeIndoors
      ? SessionAnalysisType.LOOP_VELODROME_INDOORS
      : SessionAnalysisType.LOOP_VELODROME_OUTDOORS;

    try {
      const newRoute = await createRoute.mutateAsync({
        description: newVelodromeName.trim(),
        route_category: routeCategory,
        lap_length: lapLength,
        bend_radius: bendRadius,
        banking_angle: bankAngle,
      });
      toast.success('Velodrome created successfully');
      setShowNewVelodromeForm(false);
      setNewVelodromeName('');
      setNewLapLength('');
      setNewBendRadius('');
      setNewBankAngle('');
      // Auto-select the newly created route
      if (onRouteSelect && newRoute?.id) {
        onRouteSelect(newRoute.id);
      }
    } catch (error) {
      console.error('Error creating velodrome:', error);
      toast.error('Failed to create velodrome');
    }
  };

  const handleCancelNewVelodrome = () => {
    setShowNewVelodromeForm(false);
    setNewVelodromeName('');
    setNewLapLength('');
    setNewBendRadius('');
    setNewBankAngle('');
  };

  const handleRouteClick = (routeId: string) => {
    if (onRouteSelect) {
      // Toggle selection - if already selected, deselect
      onRouteSelect(selectedRouteId === routeId ? null : routeId);
    }
  };

  if (!isVelodrome) {
    return null;
  }

  return (
    <div
      className={`cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left ${className}`}
    >
      <FormEntry label={isVelodromeIndoors ? 'Indoor Velodrome Routes' : 'Outdoor Velodrome Routes'}>
        {isRoutesLoading ? (
          <div className="cr-flex cr-items-center cr-gap-2 cr-py-2">
            <ImSpinner2 className="cr-h-5 cr-w-5 cr-animate-spin cr-text-blue-600" />
            <span className="cr-text-sm cr-text-gray-500">Loading routes...</span>
          </div>
        ) : routes && routes.length > 0 ? (
          <div className="cr-max-h-48 cr-overflow-y-auto cr-rounded cr-border cr-border-gray-200 cr-bg-gray-50">
            <ul className="cr-divide-y cr-divide-gray-200">
              {routes.map((route) => {
                const isSelected = selectedRouteId === route.id;
                return (
                  <li
                    key={route.id}
                    onClick={() => handleRouteClick(route.id)}
                    className={`cr-cursor-pointer cr-px-3 cr-py-2 cr-text-sm cr-transition-colors hover:cr-bg-gray-100 ${
                      isSelected
                        ? 'cr-bg-[#03365D] cr-text-white hover:cr-bg-[#024a7a]'
                        : 'cr-text-gray-700'
                    }`}
                  >
                    <div className="cr-flex cr-items-center cr-gap-2">
                      <div
                        className={`cr-flex cr-h-5 cr-w-5 cr-flex-shrink-0 cr-items-center cr-justify-center cr-rounded-full cr-border-2 ${
                          isSelected
                            ? 'cr-border-white cr-bg-white'
                            : 'cr-border-gray-300 cr-bg-white'
                        }`}
                      >
                        {isSelected && <HiCheck className="cr-h-3 cr-w-3 cr-text-[#03365D]" />}
                      </div>
                      <div className="cr-flex cr-flex-col">
                        <span className="cr-font-medium">{route.description || 'Unnamed Route'}</span>
                        <span className={`cr-text-xs ${isSelected ? 'cr-text-gray-200' : 'cr-text-gray-500'}`}>
                          Lap: {route.lap_length ?? '—'}m | Bend: {route.bend_radius ?? '—'}m | Bank:{' '}
                          {route.banking_angle ?? '—'}°
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <p className="cr-py-2 cr-text-sm cr-text-gray-500">
            No {isVelodromeIndoors ? 'indoor' : 'outdoor'} velodrome routes available.
          </p>
        )}

        {/* Create New Velodrome Button / Form */}
        {!showNewVelodromeForm ? (
          <button
            type="button"
            onClick={() => setShowNewVelodromeForm(true)}
            className="cr-mt-3 cr-flex cr-items-center cr-gap-1 cr-self-start cr-rounded-md cr-bg-cirrusBlue-25 cr-px-3 cr-py-1.5 cr-text-sm cr-text-[#002D50] cr-transition-colors hover:cr-bg-cirrusBlue-50"
          >
            <HiPlus className="cr-h-4 cr-w-4" />
            <span>Create New {isVelodromeIndoors ? 'Indoor' : 'Outdoor'} Velodrome</span>
          </button>
        ) : (
          <div className="cr-mt-3 cr-rounded cr-border cr-border-gray-200 cr-bg-gray-50 cr-p-3">
            <h4 className="cr-mb-3 cr-text-sm cr-font-semibold cr-text-gray-800">
              New {isVelodromeIndoors ? 'Indoor' : 'Outdoor'} Velodrome
            </h4>
            <div className="cr-flex cr-flex-col cr-gap-3">
              {/* Velodrome Name */}
              <div className="cr-flex cr-flex-col cr-gap-1">
                <label className="cr-text-xs cr-font-medium cr-text-gray-700">Velodrome Name</label>
                <input
                  type="text"
                  value={newVelodromeName}
                  onChange={(e) => setNewVelodromeName(e.target.value)}
                  placeholder="e.g., Manchester Velodrome"
                  className="cr-rounded cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                />
              </div>

              {/* Numerical Fields Row */}
              <div className="cr-flex cr-flex-wrap cr-gap-3">
                {/* Lap Length */}
                <div className="cr-flex cr-flex-col cr-gap-1">
                  <label className="cr-text-xs cr-font-medium cr-text-gray-700">Lap Length (m)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={newLapLength}
                    onChange={(e) => setNewLapLength(e.target.value)}
                    placeholder="e.g., 250"
                    className="cr-w-28 cr-rounded cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                  />
                </div>

                {/* Bend Radius */}
                <div className="cr-flex cr-flex-col cr-gap-1">
                  <label className="cr-text-xs cr-font-medium cr-text-gray-700">Bend Radius (m)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={newBendRadius}
                    onChange={(e) => setNewBendRadius(e.target.value)}
                    placeholder="e.g., 25"
                    className="cr-w-28 cr-rounded cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                  />
                </div>

                {/* Bank Angle */}
                <div className="cr-flex cr-flex-col cr-gap-1">
                  <label className="cr-text-xs cr-font-medium cr-text-gray-700">Bank Angle (°)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={newBankAngle}
                    onChange={(e) => setNewBankAngle(e.target.value)}
                    placeholder="e.g., 42"
                    className="cr-w-28 cr-rounded cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="cr-flex cr-gap-2">
                <button
                  type="button"
                  onClick={handleCreateVelodrome}
                  disabled={createRoute.isPending}
                  className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-[#03365D] cr-px-3 cr-py-1.5 cr-text-sm cr-text-white hover:cr-bg-[#024a7a] disabled:cr-opacity-50"
                >
                  {createRoute.isPending ? (
                    <ImSpinner2 className="cr-h-4 cr-w-4 cr-animate-spin" />
                  ) : (
                    <HiOutlineCheckCircle className="cr-h-4 cr-w-4" />
                  )}
                  {createRoute.isPending ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelNewVelodrome}
                  disabled={createRoute.isPending}
                  className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-gray-200 cr-px-3 cr-py-1.5 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-300 disabled:cr-opacity-50"
                >
                  <HiOutlineXMark className="cr-h-4 cr-w-4" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </FormEntry>
    </div>
  );
}
